import { FC } from 'react'

import type { SuggestionDto } from '@api'
import { noop } from 'lodash'

import { ItemWithBadge } from '@/components/ui/item-with-badge/ItemWithBadge'
import { Tooltip } from '@/components/ui/tooltip'
import { SendMessageRequestWithTranslation } from '@/models/chattingUI/chatSuggestions/chatSuggestions.ts'

import DirectoryIcon from '../../../icons/directory.svg'
import MediaIcon from '../../../icons/preview-photo.svg'
import BoxIcon from '../../../icons/tabler-icon-box.svg'
import { IconButton } from '../../components/icon-button'

interface Props {
  metadata?: SuggestionDto
  onOpenMediaPreview: () => void
}

export const ChatSuggestionMetadata: FC<Props> = ({
  metadata,
  onOpenMediaPreview,
}) => {
  const computedData = metadata?.messages?.reduce<{
    sequence: SendMessageRequestWithTranslation[]
    medias: number[]
  }>(
    (acc, message) => {
      acc.sequence.push(message)
      if (message.mediaFiles) {
        acc.medias.push(...(message.mediaFiles as unknown as number[]))
      }

      return acc
    },
    {
      sequence: [],
      medias: [],
    },
  )

  const media = computedData?.medias
  const sequence = computedData?.sequence
  const renderMessageSequence = () => {
    return (
      <div className="flex flex-col text-left">
        <div className="flex flex-col gap-1 pb-2 border-b border-border">
          <span className="text-sm font-medium">Message Sequence</span>
          <span className="text-xs text-secondary-300">
            The messages are going to be sent out one after another, with a
            short delay
          </span>
        </div>
        <div className="flex flex-col gap-3 mt-2">
          {sequence?.map((message) => (
            <div
              key={message.translatedText || message.text}
              className="flex items-center gap-1"
            >
              <DirectoryIcon className="h-4 w-4 text-default flex-shrink-0" />
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    message.translatedText ||
                    message.text ||
                    'This is a message without text',
                }}
              />
              {!!message.mediaFiles?.length && (
                <ItemWithBadge
                  size="sm"
                  badge={{ content: message.mediaFiles?.length ?? 0 }}
                  className="flex-shrink-0 self-center"
                >
                  <MediaIcon className="h-5 w-5 text-default flex-shrink-0" />
                </ItemWithBadge>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-row gap-2.5">
      {!!media?.length && (
        <Tooltip content="Media preview">
          <div onClick={onOpenMediaPreview}>
            <ItemWithBadge
              size="sm"
              badge={{ content: media?.length || 0 }}
            >
              <IconButton
                icon={MediaIcon}
                iconClassName="h-6 w-6 text-secondary-300"
              />
            </ItemWithBadge>
          </div>
        </Tooltip>
      )}
      {sequence && sequence?.length > 1 && (
        <Tooltip
          content={renderMessageSequence()}
          contentClassName="max-w-md px-3 py-2"
        >
          <ItemWithBadge
            size="sm"
            badge={{ content: sequence.length }}
          >
            <IconButton
              icon={BoxIcon}
              className="text-icon"
              iconClassName="h-6 w-6 text-secondary-300"
              onClick={noop}
            />
          </ItemWithBadge>
        </Tooltip>
      )}
    </div>
  )
}
